import { computed, ref, watch } from "vue";
import { useStore } from "vuex";

import AmountSelector from "components/AmountSelector/AmountSelector.vue";
import GetLabel from 'foundation/js/getLabel'

const _replaceLastElementInUrl = (url, replacement) => {
    // Remove trailing slash if it exists
    if (url.endsWith('/')) {
        url = url.slice(0, -1);
    }

    // Split the URL into parts
    var parts = url.split('/');

    // Replace the last part
    parts[parts.length - 1] = replacement;

    // Join the parts back together
    var newUrl = parts.join('/');

    // Ensure the new URL ends with a trailing slash
    if (!newUrl.endsWith('/')) {
        newUrl += '/';
    }

    return newUrl;
}

export default {
    name: "BuySection",
    components: { AmountSelector },
    props: {
        variants: {
            type: String,
            required: true,
        },
        currentVariantSku: {
            type: String,
            required: false,
        },
        pdf: {
            type: String,
            required: false,
        },
        soldOutText: {
            type: String,
            required: false,
        },
        soldOutReason: {
            type: String,
            required: false,
        },
        rolePageUrl: {
            type: String,
            required: false,
        },
        basketPageUrl: {
            type: String,
            required: true,
        },
        hasRequiredRights: {
            type: Boolean,
            required: true
        }
    },
    setup(props) {
        const store = useStore();

        const unSubmitted = ref();

        const parsedVariants = props.variants ? JSON.parse(props.variants) : "";
        // Adding placeholder variant to the top of list of variants. This item is meant to be
        // read only to the user. Copied data from the first item in the to make sure it has
        // the same structure and the price can be displayed
        const placeholderVariant = props.variants
            ? {
                  ...parsedVariants.variantList[0],
                  sku: "-",
                  displayName: parsedVariants.placeholder,
              }
            : "";
        const variantData = computed(() => {
            // Extend the variant list with the placeholder variant
            var variants = {
                ...parsedVariants,
                variantList: [
                    placeholderVariant,
                    ...parsedVariants.variantList,
                ],
            };
            return variants;
        });
        const pdfData = computed(() =>
            props.pdf ? JSON.parse(props.pdf) : null
        );
        const currentVariantSku = ref(
            props.currentVariantSku ?? placeholderVariant.sku,
        );
        const currentVariant = computed(() =>
            variantData.value.variantList.find(
                (variant) => variant.sku === currentVariantSku.value,
            ),
        );
        const amount = ref(variantData.value.startAmount ?? variantData.value.minAmount);
        const currentUrlName = ref(variantData.value.productUrlName);
        const setAmount = (inputAmount) => (amount.value = inputAmount);

        const hasRequiredRights = props.hasRequiredRights;
        const displayHasRequiredRightsMessage = !hasRequiredRights ? "kb-product-detail__error-message--show" : 'kb-product-detail__error-message'

        // It should update the url when the variant changes, but not 
        // until the user has picked one from the dropdownlist
        watch(currentVariant, (variant) => {
            updatePageUrlWithVariantName(variant);
        });

        // updating url with current variant/sku so user can share direct link
        const updatePageUrlWithVariantName = function (variant) {
            const currentUrl = window.location.href;
            const newUrl = _replaceLastElementInUrl(currentUrl, variant.urlName);
            window.history.replaceState("ProductDetailPage", "", newUrl);

            currentUrlName.value = variant.urlName;
        };

        const updateDisplayNoVariantChosenMessage = function () {
            if (!unSubmitted.value) {
                displayNoVariantChosenMessage.value =
                    currentVariant.value.sku === placeholderVariant.sku
                        ? "kb-product-detail__error-message--show"
                        : "";
            }
        };

        const addToCart = () => {
            // prevent submit if min/max amount is not correct
            if (amount.value < variantData.value.minAmount) return;
            if (amount.value > variantData.value.maxAmount) return;

            unSubmitted.value = false;

            updateDisplayNoVariantChosenMessage();

            if (currentVariant.value.sku === placeholderVariant.sku) {
                console.error(
                    "No variant selected, can not add to basket without"
                );
                return;
            }

            store.dispatch("basket/addToBasket", {
                sku: currentVariant.value.sku,
                quantity: amount.value,
                basketPageUrl: props.basketPageUrl,
            });
        };

        const openRequestRoleOverlay = () => {
            store.commit('main/set_page_overlay_content_data', {
                rolePageUrl: props.rolePageUrl
            })

            store.dispatch('main/openPageOverlay', {
                overlayContent: 'FrivilligeShopWrongRoleModal'
            })
        }

        const displayNoVariantChosenMessage = ref();
        const displayMaxAmountMessage = ref();
        const displayMinAmountMessage = ref();

        const displayMessage = function (show) {
            if (!unSubmitted.value) {
                if (show === "max-error") {
                    displayMinAmountMessage.value = "";
                    displayMaxAmountMessage.value = show
                        ? "kb-product-detail__error-message--show"
                        : "";
                } else if (show === "min-error") {
                    displayMaxAmountMessage.value = "";
                    displayMinAmountMessage.value = show
                        ? "kb-product-detail__error-message--show"
                        : "";
                } else {
                    displayMinAmountMessage.value = "";
                    displayMaxAmountMessage.value = "";
                }
            }
        };
        return {
            amount,
            currentVariant,
            currentVariantSku,
            displayMaxAmountMessage,
            displayMinAmountMessage,
            displayNoVariantChosenMessage,
            pdfData,
            unSubmitted,
            variantData,
            hasRequiredRights,
            displayHasRequiredRightsMessage,
            addToCart,
            openRequestRoleOverlay,
            displayMessage,
            getLabel: (labelKey, params, allowEmpty = false) => GetLabel({ labelKey, labelCollection: 'buySection', params, allowEmpty}),
            setAmount,
            updateDisplayNoVariantChosenMessage,
        };
    },
};
